
import React, { useState } from 'react';
import { AlertCircle, AlertTriangle, ArrowRight, Check, ChevronDown, Edit, Eye, Search, Trash2, X } from 'lucide-react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import MoveProductModal from './MoveProductModal';

const ManageCategoriesModal = ({
    categories,
    setCategories,
    onClose,
    products = [], // Pass products to check linkage
    onCategoriesChange, // Callback to refresh parent data if needed
    onProductsChange // Callback when products are modified (moved/deleted)
}) => {
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [viewingLinkedCat, setViewingLinkedCat] = useState(null); // Category ID to view linked products
    const [searchTerm, setSearchTerm] = useState('');

    // Resolution Dialog State
    const [resolveDialog, setResolveDialog] = useState({
        show: false,
        categoryId: null,
        categoryName: '',
        productCount: 0,
        step: 'choice' // 'choice', 'move', 'confirm_delete'
    });
    const [targetCategory, setTargetCategory] = useState('');
    const [showMoveProductModal, setShowMoveProductModal] = useState(false);
    const [productToMove, setProductToMove] = useState(null);

    const handleMoveProductClick = (product) => {
        setProductToMove(product);
        setShowMoveProductModal(true);
    };

    const handleMoveSuccess = () => {
        if (onProductsChange) onProductsChange(); // Refresh data
        setShowMoveProductModal(false);
        setProductToMove(null);
    };

    const handleDeleteSingleProduct = async (product) => {
        if (window.confirm(`Are you sure you want to delete "${product.product?.name || 'this product'}"? This action cannot be undone.`)) {
            try {
                await axios.delete(`/admin/products/${product.id || product._id}`);
                toast.success('Product deleted successfully');
                if (onProductsChange) onProductsChange(); // Refresh data
            } catch (error) {
                console.error('Error deleting product:', error);
                toast.error('Failed to delete product');
            }
        }
    };

    const getLinkedProducts = (catId) => {
        return products.filter(p => {
            // Check for both direct assignment and nested object assignment
            const pCatId = p.product?.categoryId;
            return (typeof pCatId === 'object' ? pCatId?._id === catId : pCatId === catId) ||
                p.categoryId === catId;
        });
    };

    const filteredCategories = categories.filter(c =>
        !c.isPending &&
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleUpdateCategory = async (categoryId, newName) => {
        try {
            await axios.put(`/admin/categories/${categoryId}`, {
                name: newName
            });

            const updatedCategories = categories.map(c =>
                c.id === categoryId ? { ...c, name: newName } : c
            );
            setCategories(updatedCategories);
            if (onCategoriesChange) onCategoriesChange(updatedCategories);
            toast.success('Category updated successfully');
        } catch (error) {
            console.error('Error updating category:', error);
            toast.error('Failed to update category');
        }
    };

    const handleDeleteClick = async (category) => {
        const linkedCount = getLinkedProducts(category.id).length;

        if (linkedCount > 0) {
            setResolveDialog({
                show: true,
                categoryId: category.id,
                categoryName: category.name,
                productCount: linkedCount,
                step: 'choice'
            });
        } else {
            // Normal delete
            if (!window.confirm('Are you sure you want to delete this category?')) return;
            await performDelete(category.id);
        }
    };

    const performDelete = async (categoryId) => {
        try {
            await axios.delete(`/admin/categories/${categoryId}`);
            const updatedCategories = categories.filter(c => c.id !== categoryId);
            setCategories(updatedCategories);
            if (onCategoriesChange) onCategoriesChange(updatedCategories);
            toast.success('Category deleted successfully');
            setResolveDialog({ show: false, categoryId: null, categoryName: '', productCount: 0, step: 'choice' });
        } catch (error) {
            console.error('Error deleting category:', error);

            // Fallback if backend reports conflicts we didn't catch locally
            if (error.response?.status === 409) {
                setResolveDialog({
                    show: true,
                    categoryId: categoryId,
                    categoryName: categories.find(c => c.id === categoryId)?.name || 'Unknown',
                    productCount: error.response.data.details?.productCount || 'some',
                    step: 'choice'
                });
            } else {
                const errorMsg = error.response?.data?.error || 'Failed to delete category';
                toast.error(errorMsg);
            }
        }
    };

    const handleMoveProducts = async () => {
        if (!targetCategory) {
            toast.error('Please select a target category');
            return;
        }

        try {
            await axios.post(`/admin/categories/${resolveDialog.categoryId}/products/move`, {
                targetCategoryId: targetCategory
            });
            toast.success('Products moved successfully');

            // After moving, delete the empty category
            await performDelete(resolveDialog.categoryId);

            // We should probably trigger a full refresh in parent to update product list
            window.location.reload(); // Simple way to ensure specific state is consistent, or we can use callback
            // Ideally calls a prop like refreshData()
        } catch (error) {
            console.error('Error moving products:', error);
            toast.error('Failed to move products');
        }
    };

    const handleDeleteProducts = async () => {
        try {
            await axios.delete(`/admin/categories/${resolveDialog.categoryId}/products`);
            toast.success('Products deleted successfully');

            // After deleting products, delete the category
            await performDelete(resolveDialog.categoryId);

            // Refresh to update product list
            window.location.reload();
        } catch (error) {
            console.error('Error deleting products:', error);
            toast.error('Failed to delete products');
        }
    };

    // Sub-component for resolution dialog
    const ResolutionDialog = () => {
        if (!resolveDialog.show) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
                <div className="bg-white rounded-lg w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                    <div className="p-6">
                        <div className="flex items-center space-x-3 text-amber-600 mb-4">
                            <AlertTriangle className="h-6 w-6" />
                            <h3 className="text-lg font-bold">Cannot Delete Category</h3>
                        </div>

                        <p className="text-gray-600 mb-6">
                            The category <strong>"{resolveDialog.categoryName}"</strong> has <strong>{resolveDialog.productCount}</strong> linked products.
                            You must resolve these dependencies before deleting.
                        </p>

                        {resolveDialog.step === 'choice' && (
                            <div className="space-y-3">
                                <button
                                    onClick={() => setResolveDialog(prev => ({ ...prev, step: 'move' }))}
                                    className="w-full flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 hover:border-blue-500 transition-all group"
                                >
                                    <div className="text-left">
                                        <span className="font-semibold text-slate-800 block">Move Products</span>
                                        <span className="text-sm text-gray-500">Transfer products to another category</span>
                                    </div>
                                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500" />
                                </button>

                                <button
                                    onClick={() => setResolveDialog(prev => ({ ...prev, step: 'confirm_delete' }))}
                                    className="w-full flex items-center justify-between p-4 border rounded-lg hover:bg-red-50 hover:border-red-500 transition-all group"
                                >
                                    <div className="text-left">
                                        <span className="font-semibold text-red-700 block">Delete Products</span>
                                        <span className="text-sm text-red-600/70">Permanently delete all linked products</span>
                                    </div>
                                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-red-500" />
                                </button>
                            </div>
                        )}

                        {resolveDialog.step === 'move' && (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Target Category</label>
                                    <select
                                        value={targetCategory}
                                        onChange={(e) => setTargetCategory(e.target.value)}
                                        className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="">-- Select Category --</option>
                                        {categories
                                            .filter(c => c.id !== resolveDialog.categoryId && !c.isPending)
                                            .map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                                <div className="flex space-x-3 pt-2">
                                    <button
                                        onClick={() => setResolveDialog(prev => ({ ...prev, step: 'choice' }))}
                                        className="flex-1 px-4 py-2 border rounded text-gray-600 hover:bg-gray-50"
                                    >
                                        Back
                                    </button>
                                    <button
                                        onClick={handleMoveProducts}
                                        disabled={!targetCategory}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        Move & Delete
                                    </button>
                                </div>
                            </div>
                        )}

                        {resolveDialog.step === 'confirm_delete' && (
                            <div className="space-y-4 bg-red-50 p-4 rounded-lg border border-red-100">
                                <div className="flex items-start space-x-3">
                                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                                    <div>
                                        <h4 className="font-bold text-red-800">Warning: Irreversible Action</h4>
                                        <p className="text-sm text-red-700 mt-1">
                                            This will permanently delete <strong>{resolveDialog.productCount}</strong> products.
                                            History in Estimates/Invoices will remain safe, but these products will be removed from inventory.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex space-x-3 pt-2">
                                    <button
                                        onClick={() => setResolveDialog(prev => ({ ...prev, step: 'choice' }))}
                                        className="flex-1 px-4 py-2 bg-white border border-red-200 text-red-700 rounded hover:bg-red-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeleteProducts}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                                    >
                                        Confirm Delete
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {resolveDialog.step === 'choice' && (
                        <div className="bg-gray-50 p-4 flex justify-end">
                            <button
                                onClick={() => setResolveDialog({ show: false, categoryId: null, categoryName: '', productCount: 0, step: 'choice' })}
                                className="text-gray-500 hover:text-gray-700 px-4 py-2"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b bg-[#0A5F38] rounded-t-lg">
                    <h3 className="text-xl font-bold text-white">Category Management</h3>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-6">
                    {!viewingLinkedCat && (
                        <div className="mb-4 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search categories..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 block w-full border-gray-300 rounded-md focus:ring-[#C5A57D] focus:border-[#C5A57D] sm:text-sm py-2 border shadow-sm"
                            />
                        </div>
                    )}

                    {viewingLinkedCat ? (
                        <div className="space-y-4">
                            <div className="flex items-center space-x-2 mb-4">
                                <button
                                    onClick={() => setViewingLinkedCat(null)}
                                    className="text-[#0A5F38] hover:underline flex items-center"
                                >
                                    <ChevronDown className="h-4 w-4 rotate-90 mr-1" /> Back to Categories
                                </button>
                                <span className="text-gray-400">|</span>
                                <h4 className="font-semibold text-lg text-slate-800">
                                    Products in "{categories.find(c => c.id === viewingLinkedCat)?.name}"
                                </h4>
                            </div>

                            <div className="overflow-x-auto border rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {getLinkedProducts(viewingLinkedCat).length > 0 ? (
                                            getLinkedProducts(viewingLinkedCat).map(prod => (
                                                <tr key={prod.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 text-sm font-medium text-[#0A5F38]">{prod.product?.name}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">{prod.product?.sku}</td>
                                                    <td className="px-6 py-4 text-sm text-slate-900">₹{prod.product?.sellingPrice}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">{prod.product?.quantity}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <div className="flex items-center justify-end space-x-3">
                                                            <button
                                                                onClick={() => handleMoveProductClick(prod)}
                                                                className="text-blue-600 hover:text-blue-800"
                                                                title="Move to Category"
                                                            >
                                                                <ArrowRight className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteSingleProduct(prod)}
                                                                className="text-red-500 hover:text-red-700"
                                                                title="Delete Product"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="px-6 py-8 text-center text-gray-500 italic">
                                                    No products found in this category.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto border rounded-lg pl-4">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-[#fcfbf7]">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-bold text-[#0A5F38] uppercase tracking-wider">Category Name</th>
                                        <th className="px-6 py-3 text-center text-xs font-bold text-[#0A5F38] uppercase tracking-wider">Linked Products</th>
                                        <th className="px-6 py-3 text-right text-xs font-bold text-[#0A5F38] uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredCategories.length > 0 ? (
                                        filteredCategories.map((category) => {
                                            const linkedCount = getLinkedProducts(category.id).length;
                                            const isEditing = editingId === category.id;

                                            return (
                                                <tr key={category.id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {isEditing ? (
                                                            <div className="flex items-center space-x-2">
                                                                <input
                                                                    type="text"
                                                                    value={editName}
                                                                    onChange={(e) => setEditName(e.target.value)}
                                                                    className="border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-[#C5A57D] outline-none"
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    onClick={() => {
                                                                        if (editName.trim()) {
                                                                            handleUpdateCategory(category.id, editName);
                                                                            setEditingId(null);
                                                                        }
                                                                    }}
                                                                    className="text-green-600 hover:text-green-800"
                                                                >
                                                                    <Check className="h-4 w-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => setEditingId(null)}
                                                                    className="text-red-500 hover:text-red-700"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col">
                                                                <span className="font-medium text-slate-800">{category.name}</span>
                                                                {category.isOthers && <span className="text-xs text-amber-600 italic">"Others" Type</span>}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span
                                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${linkedCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}
                                                        >
                                                            {linkedCount} Products
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <div className="flex items-center justify-end space-x-3">
                                                            <button
                                                                onClick={() => setViewingLinkedCat(category.id)}
                                                                className={`${category.isOthers ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-900'}`}
                                                                title={category.isOthers ? "Cannot view details for 'Others' category" : "View Linked Products"}
                                                                disabled={category.isOthers}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingId(category.id);
                                                                    setEditName(category.name);
                                                                }}
                                                                className="text-[#C5A57D] hover:text-[#B8985E]"
                                                                title="Edit Category"
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteClick(category)}
                                                                className={`${(category.isOthers) ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`}
                                                                title={
                                                                    category.isOthers
                                                                        ? "Cannot delete 'Others' category"
                                                                        : "Delete Category"
                                                                }
                                                                disabled={category.isOthers}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                                                No categories found matching "{searchTerm}".
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t bg-gray-50 rounded-b-lg flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
                    >
                        Close
                    </button>
                </div>

                {/* Resolution Dialog Overlay */}
                <ResolutionDialog />
            </div>

            {/* Move Product Modal */}
            {showMoveProductModal && (
                <MoveProductModal
                    isOpen={showMoveProductModal}
                    onClose={() => setShowMoveProductModal(false)}
                    product={productToMove}
                    categories={categories}
                    onMoveSuccess={handleMoveSuccess}
                />
            )}
        </div>
    );
};

export default ManageCategoriesModal;
